import clsx from 'clsx';
import A from 'components/atoms/A/A';
import Alert from 'components/atoms/Alert/Alert';
import Button from 'components/atoms/Button/Button';
import LoadingOverlay from 'components/atoms/LoadingOverlay/LoadingOverlay';
import RegistrationCard from 'components/atoms/RegistrationCard/RegistrationCard';
import PasswordInput, {
  PASSWORD_REQUIREMENT_DESCRIPTIONS,
} from 'components/atoms/TextInput/PasswordInput';
import SecondFactorCode from 'components/atoms/TextInput/SecondFactorCode';
import Txt from 'components/atoms/Txt/Txt';
import Form from 'components/molecules/Form/Form';
import { submitResetPassword } from 'components/templates/AuthenticationProvider/authApiUtils';
import { AuthenticationContext } from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import { SecondFactorRequiredError } from 'components/templates/AuthenticationProvider/SecondFactorRequiredError';
import { ResetPasswordError } from 'components/templates/AuthenticationProvider/Types';
import { useCountdownTimer } from 'hooks/useCountdownTimer';
import usePromiseState from 'hooks/usePromiseState';
import { useRouter } from 'next/router';
import React, { useContext } from 'react';
import classes from './ResetPassword.module.scss';
import { ResetSuccess } from './ResetSuccess';

export default function ResetPassword() {
  const router = useRouter();
  const { locale, query, push } = router;
  const [isLoggingIn, setIsLoggingIn] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [handlePasswordReset, resetPasswordRequestState] = usePromiseState(submitResetPassword);
  const resetToken = query.reset_password_token;
  const { logOut } = useContext(AuthenticationContext);
  const { startCountdown } = useCountdownTimer('secondFactorCountdown');

  const [requiresSecondFactor, setRequiresSecondFactor] = React.useState<string | undefined>(
    Array.isArray(query.session_id) ? query.session_id[0] : query.session_id,
  );

  const [showSecondFactor, setShowSecondFactor] = React.useState<boolean>(false);
  const [passwords, setPasswords] = React.useState<{ password: string; confirmPassword: string }>({
    password: '',
    confirmPassword: '',
  });
  const [allOtpFilled, setAllOtpFilled] = React.useState(false);

  // Calls the reset API and reacts to the response. Used by both the password form and the OTP form.
  // If the backend requires OTP it throws SecondFactorRequiredError, which switches us to the OTP form.
  const performReset = async (password: string, confirmPassword: string, twoFaCode?: string) => {
    setIsLoggingIn(true);
    try {
      await handlePasswordReset(
        { locale },
        password,
        confirmPassword,
        Array.isArray(resetToken) ? resetToken[0] : resetToken,
        requiresSecondFactor,
        twoFaCode,
      );
      logOut('reset-password');
      setSuccess(true);
      setShowSecondFactor(false);
    } catch (err) {
      if (err instanceof SecondFactorRequiredError) {
        setRequiresSecondFactor(err.getSessionId());
        setShowSecondFactor(true);
        startCountdown();
      } else if (err instanceof ResetPasswordError) {
        setError(err.updateError);
      } else {
        setError('There was an error resetting your password, please try again.');
      }
    }
    setIsLoggingIn(false);
  };

  const handlePasswordFormSubmit = async (formData: FormData) => {
    setError(undefined);
    const password = formData.get('password')?.toString() || '';
    const confirmPassword = formData.get('confirmPassword')?.toString() || '';

    if (!password || !confirmPassword || password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (!resetToken && !requiresSecondFactor) return;

    setPasswords({ password, confirmPassword });

    // If we already have a session_id from the URL, OTP is known to be required — skip to it.
    if (requiresSecondFactor) {
      setShowSecondFactor(true);
      startCountdown();
      return;
    }

    await performReset(password, confirmPassword);
  };

  const handleSecondFactorSubmit = async (formData: FormData) => {
    setError(undefined);
    if (
      (resetToken || requiresSecondFactor) &&
      passwords.password &&
      passwords.confirmPassword &&
      passwords.password === passwords.confirmPassword
    ) {
      await performReset(
        passwords.password,
        passwords.confirmPassword,
        formData.get('2fa_code')?.toString(),
      );
    }
  };

  return (
    <RegistrationCard
      withReportProblem={!success}
      title={showSecondFactor || success ? '' : 'RESET PASSWORD'}
      hasUppercaseTitle
      narrowContainer
    >
      {success && !showSecondFactor ? (
        <ResetSuccess />
      ) : (
        <>
          {(success && resetPasswordRequestState.isLoading) ||
            (isLoggingIn && (
              <Alert
                title={'Success'}
                subtitle={'Password reset successful'}
                type={'success'}
                onClose={() => {
                  setSuccess(false);
                }}
              />
            ))}
          {(resetPasswordRequestState.isLoading || isLoggingIn) && <LoadingOverlay />}
          {(error || (!resetToken && !requiresSecondFactor)) && (
            <Alert
              title={'Please try again'}
              subtitle={
                error ||
                'Your reset password link is not valid. Please try resetting your password again'
              }
              type={'error'}
              onClose={() => {
                setError(undefined);
              }}
            />
          )}
          {!showSecondFactor && (resetToken || requiresSecondFactor) && (
            <>
              <Txt component={'p'} className={clsx(classes.getNew)}>
                Choose a unique password.
              </Txt>
              <Txt className={clsx(classes.instructions)} component={'p'}>
                {PASSWORD_REQUIREMENT_DESCRIPTIONS}
              </Txt>
              <Form
                className={classes.form}
                submitDisabled={resetPasswordRequestState.isLoading}
                submitName={'Reset password'}
                submitButtonType={'primary'}
                buttonWrapperClassName={classes.buttonWrapper}
                onSubmit={handlePasswordFormSubmit}
              >
                <PasswordInput
                  id={'password'}
                  title="New password"
                  placeholder="Enter new password"
                  required
                  autoComplete={'new-password'}
                  minLength={8}
                  overrideErrorMessage={(_validity, _value) => {
                    return PASSWORD_REQUIREMENT_DESCRIPTIONS;
                  }}
                />
                <PasswordInput
                  id={'confirmPassword'}
                  title="Confirm new password"
                  placeholder="Re-enter new password"
                  required
                  autoComplete={'new-password'}
                  minLength={8}
                  overrideErrorMessage={(_validity, _value) => {
                    return PASSWORD_REQUIREMENT_DESCRIPTIONS;
                  }}
                />
              </Form>
            </>
          )}
          {showSecondFactor && (
            <Form
              className={classes.form}
              submitDisabled={!allOtpFilled}
              submitName={'Verify'}
              submitButtonType={'primary'}
              buttonWrapperClassName={classes.buttonWrapper}
              onSubmit={handleSecondFactorSubmit}
            >
              <SecondFactorCode
                returnHref="/"
                actionText="Return to login"
                initialText="Return to login in "
                username={String(query.username) || ''}
                onAllInputsFilled={setAllOtpFilled}
              />
            </Form>
          )}
          {!resetToken && !requiresSecondFactor && !showSecondFactor && (
            <Button
              buttonType="secondary"
              buttonSize="large"
              className={classes.tryAgain}
              component={A}
              href="/forgot-password"
            >
              <Txt>Try again</Txt>
            </Button>
          )}
        </>
      )}
    </RegistrationCard>
  );
}
